'use client';

import React, { useState, useEffect } from 'react';
import FAQSearch from './FAQSearch';
import FAQCategories from './FAQCategories';
import FAQItem from './FAQItem';
import FAQGlossary from './FAQGlossary';
import FAQContact from './FAQContact';
import Icon from '@/components/ui/AppIcon';


interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  relatedLinks?: Array<{
    title: string;
    url: string;
    type: 'case-study' | 'blog' | 'service';
  }>;
  videoUrl?: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
}

const FAQInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
    { id: 'all', name: 'Alle', icon: 'Squares2X2Icon', count: 42 },
    { id: 'services', name: 'Diensten', icon: 'CubeIcon', count: 12 },
    { id: 'implementation', name: 'Implementatie', icon: 'CogIcon', count: 8 },
    { id: 'pricing', name: 'Prijzen', icon: 'CurrencyEuroIcon', count: 6 },
    { id: 'technical', name: 'Technisch', icon: 'CommandLineIcon', count: 10 },
    { id: 'business', name: 'Zakelijk', icon: 'BriefcaseIcon', count: 6 }
  ];

  const faqs: FAQ[] = [
    {
      id: '1',
      category: 'services',
      question: 'Welke AI-diensten biedt AIM Agency aan?',
      answer: 'AIM Agency biedt een compleet pakket AI-oplossingen speciaal ontwikkeld voor MKB-bedrijven in de Benelux. Onze diensten omvatten AI-chatbots voor klantenservice, geautomatiseerde social media content creatie, intelligente e-mail marketing, voorspellende analytics voor verkoop, geautomatiseerde documentverwerking, AI-gestuurde CRM-systemen, voice assistants voor interne processen, en gepersonaliseerde aanbevelingssystemen. Elk van deze diensten is volledig aanpasbaar aan jouw specifieke bedrijfsbehoeften en kan zowel standalone als geïntegreerd worden geïmplementeerd.',
      relatedLinks: [
        { title: 'Overzicht van alle diensten', url: '/services-overview', type: 'service' },
        { title: 'Case study: E-commerce automatisering', url: '/case-study-detail', type: 'case-study' }
      ]
    },
    {
      id: '2',
      category: 'implementation',
      question: 'Hoe lang duurt een typische AI-implementatie?',
      answer: 'De implementatieduur varieert afhankelijk van de complexiteit van de oplossing en de integratie met bestaande systemen. Een standaard chatbot implementatie duurt gemiddeld 4-6 weken, van initiële consultatie tot volledige deployment. Complexere oplossingen zoals voorspellende analytics of volledig geïntegreerde AI-systemen kunnen 8-12 weken in beslag nemen. We werken volgens een gefaseerde aanpak waarbij je al binnen 2-3 weken eerste resultaten ziet. Onze projectmanagers houden je gedurende het hele proces op de hoogte met wekelijkse updates en demo\'s.',
      relatedLinks: [
        { title: 'Implementatieproces uitgelegd', url: '/blog', type: 'blog' }
      ],
      videoUrl: 'https://example.com/implementation-video'
    },
    {
      id: '3',
      category: 'pricing',
      question: 'Wat zijn de kosten van AI-implementatie?',
      answer: 'Onze prijzen zijn transparant en gebaseerd op drie hoofdpakketten: Starter (vanaf €2.500/maand), Professional (vanaf €5.000/maand), en Enterprise (vanaf €10.000/maand). Deze pakketten omvatten implementatie, onderhoud, support en regelmatige updates. We bieden ook maatwerk oplossingen waarbij de prijs wordt bepaald op basis van jouw specifieke behoeften. Belangrijker dan de initiële investering is de ROI: onze klanten zien gemiddeld binnen 6-9 maanden een positief rendement door kostenbesparingen en efficiëntiewinst. We bieden ook een gratis ROI-calculator aan om je potentiële besparingen te berekenen.',
      relatedLinks: [
        { title: 'Bekijk onze pakketten', url: '/pricing', type: 'service' },
        { title: 'ROI calculator', url: '/pricing', type: 'service' }
      ]
    },
    {
      id: '4',
      category: 'technical',
      question: 'Welke technische vereisten zijn er voor AI-implementatie?',
      answer: 'Onze AI-oplossingen zijn ontworpen om naadloos te integreren met de meeste moderne bedrijfssystemen. Minimale vereisten zijn: een stabiele internetverbinding, toegang tot je bestaande systemen via API\'s of webhooks, en moderne browsers voor het dashboard. We ondersteunen integratie met populaire platforms zoals Salesforce, HubSpot, Shopify, WooCommerce, en vele andere. Voor on-premise oplossingen hebben we specifieke server requirements die we tijdens de consultatie bespreken. Ons technisch team voert altijd eerst een grondige analyse uit om compatibiliteit te garanderen.',
      relatedLinks: [
        { title: 'Technische specificaties', url: '/blog', type: 'blog' }
      ]
    },
    {
      id: '5',
      category: 'business',
      question: 'Hoe meet ik het succes van AI-implementatie?',
      answer: 'We definiëren samen met jou duidelijke KPI\'s voordat we starten. Typische meetpunten zijn: tijdsbesparing (uren per week), kostenbesparing (€ per maand), klanttevredenheid (CSAT score), conversieratio\'s, en response tijden. Ons dashboard biedt real-time inzicht in al deze metrics. We plannen ook maandelijkse review sessies waarin we de resultaten analyseren en optimalisaties voorstellen. Gemiddeld zien onze klanten 40% tijdsbesparing in repetitieve taken, 25% verbetering in klanttevredenheid, en 30% kostenbesparing binnen het eerste jaar.',
      relatedLinks: [
        { title: 'Case study met ROI metrics', url: '/case-study-detail', type: 'case-study' }
      ]
    },
    {
      id: '6',
      category: 'services',
      question: 'Kunnen jullie AI-oplossingen integreren met mijn bestaande systemen?',
      answer: 'Ja, integratie met bestaande systemen is een kernonderdeel van onze dienstverlening. We hebben ervaring met het koppelen van AI-oplossingen aan CRM-systemen, ERP-software, e-commerce platforms, marketing automation tools, en vele andere bedrijfssystemen. Onze technische architecten voeren eerst een integratie-analyse uit om de beste aanpak te bepalen. We gebruiken standaard API\'s waar mogelijk en ontwikkelen custom connectoren indien nodig. De integratie wordt altijd eerst getest in een staging omgeving voordat we live gaan.',
      relatedLinks: [
        { title: 'Integratie mogelijkheden', url: '/services-overview', type: 'service' }
      ]
    },
    {
      id: '7',
      category: 'implementation',
      question: 'Wat gebeurt er na de implementatie?',
      answer: 'Na succesvolle implementatie begint onze continue support en optimalisatie fase. Je krijgt toegang tot ons support team via chat, email en telefoon tijdens kantooruren. We monitoren de performance van je AI-systemen proactief en voeren regelmatig updates uit. Maandelijks ontvang je een performance rapport met aanbevelingen voor optimalisaties. We bieden ook training sessies voor nieuwe teamleden en organiseren kwartaal reviews om te kijken naar uitbreidingsmogelijkheden. Je AI-systeem wordt continue getraind met nieuwe data om de performance te verbeteren.',
      relatedLinks: [
        { title: 'Support en onderhoud', url: '/services-overview', type: 'service' }
      ]
    },
    {
      id: '8',
      category: 'pricing',
      question: 'Zijn er verborgen kosten?',
      answer: 'Nee, we geloven in volledige transparantie. Alle kosten worden vooraf duidelijk gecommuniceerd in je offerte. Dit omvat implementatie, licenties, training, support en onderhoud. De enige variabele kosten zijn usage-based fees voor bepaalde diensten (zoals API calls boven een bepaald volume), maar ook hiervoor geven we duidelijke limieten en waarschuwingen. Er zijn geen verrassingen aan het einde van de maand. Alle prijzen zijn inclusief BTW en we bieden flexibele betalingsopties.',
      relatedLinks: [
        { title: 'Transparante prijsopbouw', url: '/pricing', type: 'service' }
      ]
    },
    {
      id: '9',
      category: 'technical',
      question: 'Hoe wordt mijn data beveiligd?',
      answer: 'Data security is onze hoogste prioriteit. We zijn volledig GDPR-compliant en gebruiken enterprise-grade encryptie voor data in transit en at rest. Alle data wordt opgeslagen op servers binnen de EU. We voeren regelmatig security audits uit en hebben ISO 27001 certificering. Je behoudt altijd volledige eigendom van je data en kan op elk moment een export aanvragen. We hebben strikte toegangscontroles en logging van alle data-toegang. Voor extra gevoelige data bieden we ook on-premise deployment opties aan.',
      relatedLinks: [
        { title: 'Security en compliance', url: '/blog', type: 'blog' }
      ]
    },
    {
      id: '10',
      category: 'business',
      question: 'Wat als AI niet werkt voor mijn bedrijf?',
      answer: 'We beginnen altijd met een gratis AI-readiness assessment om te bepalen of AI geschikt is voor jouw specifieke situatie. Als we tijdens de consultatie concluderen dat AI niet de juiste oplossing is, adviseren we dat eerlijk. Voor klanten die wel starten bieden we een 30-dagen geld-terug-garantie. Als je na de eerste maand niet tevreden bent met de resultaten, krijg je je investering volledig terug. We zijn zo zeker van onze oplossingen omdat we alleen projecten aannemen waar we echt waarde kunnen toevoegen.',
      relatedLinks: [
        { title: 'Gratis AI-scan aanvragen', url: '/contact', type: 'service' }
      ]
    },
    {
      id: '11',
      category: 'services',
      question: 'Kunnen jullie ook maatwerk AI-oplossingen ontwikkelen?',
      answer: 'Absoluut! Naast onze standaard diensten ontwikkelen we ook volledig custom AI-oplossingen. Dit kan variëren van specifieke machine learning modellen voor jouw industrie tot complete AI-platforms. We starten met een discovery fase waarin we jouw unieke uitdagingen en doelen in kaart brengen. Vervolgens ontwikkelen we een proof of concept, gevolgd door iteratieve ontwikkeling met regelmatige feedback momenten. Maatwerk projecten starten vanaf €15.000 en de ontwikkeltijd varieert van 8-16 weken afhankelijk van de complexiteit.',
      relatedLinks: [
        { title: 'Maatwerk diensten', url: '/services-overview', type: 'service' }
      ]
    },
    {
      id: '12',
      category: 'implementation',
      question: 'Hoeveel tijd moet mijn team investeren in de implementatie?',
      answer: 'We minimaliseren de tijdsinvestering van jouw team. Gemiddeld hebben we 4-6 uur per week nodig van een key stakeholder voor feedback en besluitvorming. Voor de initiële setup en training plannen we 2-3 sessies van elk 2 uur. De daadwerkelijke implementatie wordt volledig door ons team uitgevoerd. We werken volgens een agile methodiek met korte sprints, zodat je team niet overbelast wordt. Na go-live is er minimale tijdsinvestering nodig voor het dagelijks gebruik - onze systemen zijn ontworpen om autonoom te werken.',
      relatedLinks: [
        { title: 'Implementatie proces', url: '/blog', type: 'blog' }
      ]
    }
  ];

  const glossaryTerms: GlossaryTerm[] = [
    {
      term: 'Artificial Intelligence (AI)',
      definition: 'Kunstmatige intelligentie is technologie die computers in staat stelt om taken uit te voeren die normaal menselijke intelligentie vereisen, zoals leren, redeneren en probleemoplossing. In zakelijke context wordt AI gebruikt voor automatisering, data-analyse en besluitvorming.',
      category: 'Basis Concepten'
    },
    {
      term: 'API (Application Programming Interface)',
      definition: 'Een set regels en protocollen waarmee verschillende software applicaties met elkaar kunnen communiceren. API\'s maken het mogelijk om AI-systemen te integreren met bestaande bedrijfssoftware zonder de broncode te wijzigen.',
      category: 'Technisch'
    },
    {
      term: 'Chatbot',
      definition: 'Een AI-aangedreven conversatie-interface die automatisch kan communiceren met klanten via tekst of spraak. Chatbots kunnen vragen beantwoorden, taken uitvoeren en klanten doorverwijzen naar de juiste informatie of medewerkers.',
      category: 'AI Toepassingen'
    },
    {
      term: 'Deep Learning',
      definition: 'Een geavanceerde vorm van machine learning die gebruik maakt van kunstmatige neurale netwerken met meerdere lagen. Deep learning is bijzonder effectief voor complexe taken zoals beeldherkenning, spraakverwerking en natuurlijke taalverwerking.',
      category: 'Technisch'
    },
    {
      term: 'GDPR Compliance',
      definition: 'Naleving van de Algemene Verordening Gegevensbescherming, de Europese privacywet die bepaalt hoe bedrijven persoonlijke data moeten verzamelen, opslaan en verwerken. Essentieel voor alle AI-systemen die met klantdata werken.',
      category: 'Juridisch'
    },
    {
      term: 'Machine Learning',
      definition: 'Een subset van AI waarbij computers leren van data zonder expliciet geprogrammeerd te worden. Het systeem verbetert automatisch door ervaring en kan patronen herkennen en voorspellingen doen op basis van historische data.',
      category: 'Basis Concepten'
    },
    {
      term: 'Natural Language Processing (NLP)',
      definition: 'Technologie die computers in staat stelt menselijke taal te begrijpen, interpreteren en genereren. NLP wordt gebruikt in chatbots, sentiment analyse, automatische vertalingen en content generatie.',
      category: 'AI Toepassingen'
    },
    {
      term: 'Predictive Analytics',
      definition: 'Het gebruik van data, statistische algoritmes en machine learning technieken om de waarschijnlijkheid van toekomstige uitkomsten te voorspellen. Gebruikt voor verkoopprognoses, klantgedrag voorspelling en risico-analyse.',
      category: 'AI Toepassingen'
    },
    {
      term: 'ROI (Return on Investment)',
      definition: 'Een maatstaf die de winstgevendheid van een investering meet door de opbrengst te vergelijken met de kosten. Voor AI-projecten wordt ROI berekend door tijdsbesparing, kostenbesparing en omzetgroei te meten tegen de implementatiekosten.',
      category: 'Zakelijk'
    },
    {
      term: 'Training Data',
      definition: 'De dataset die gebruikt wordt om een AI-model te trainen. Hoe meer kwalitatieve en relevante training data, hoe beter het model presteert. Voor zakelijke AI-toepassingen wordt vaak gebruik gemaakt van historische bedrijfsdata.',
      category: 'Technisch'
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
                Veelgestelde Vragen
              </h1>
              <p className="text-xl text-muted-foreground">
                Vind antwoorden op al je vragen over AI-implementatie
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Veelgestelde Vragen
            </h1>
            <p className="text-xl text-muted-foreground">
              Vind antwoorden op al je vragen over AI-implementatie
            </p>
          </div>

          <FAQSearch 
            onSearch={setSearchQuery}
            totalResults={filteredFAQs.length}
          />

          <FAQCategories
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />

          <div className="mb-12">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map(faq => (
                <FAQItem
                  key={faq.id}
                  question={faq.question}
                  answer={faq.answer}
                  relatedLinks={faq.relatedLinks}
                  videoUrl={faq.videoUrl}
                  onFeedback={(helpful) => {
                    console.log(`Feedback for ${faq.id}: ${helpful ? 'helpful' : 'not helpful'}`);
                  }}
                />
              ))
            ) : (
              <div className="glassmorphic rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="MagnifyingGlassIcon" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Geen resultaten gevonden
                </h3>
                <p className="text-muted-foreground mb-6">
                  Probeer een andere zoekterm of categorie
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-300"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>

          <FAQGlossary terms={glossaryTerms} />

          <div className="mt-12">
            <FAQContact />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQInteractive;